// TODO: Overhaul — delete this file once the Auto-rule endpoints are wired (#462).
import dayjs from 'dayjs';
import { Client } from 'openapi-fetch';

import { nextAutoRuleRun } from '@views/investor/move-and-invest/auto-rules/auto-rules.helper';
import { AccountsOf } from '@views/investor/queries/investment-accounts/investment-accounts.mock';
import { mockDelay as delay, ok, randomLatencyMs } from '@views/investor/queries/overhaul-mock.util';
import {
  canReceiveFunds,
  investmentAccountDisplayName,
  isLegacyAccount,
  isWallet,
} from '@views/investor/queries/queries.helper';

import {
  SystemDayOfWeek as DayOfWeek,
  SquirrelMoneyCoreContractsInvestmentAccountRuleDto as InvestmentAccountRuleDto,
  SquirrelMoneyCoreContractsInvestmentAccountRuleForm as InvestmentAccountRuleForm,
  SquirrelMoneyContractsEnumerationsInvestmentAccountStatuses as InvestmentAccountStatuses,
  SquirrelMoneyContractsTypesRuleFrequency as RuleFrequency,
  SquirrelMoneyContractsEnumerationsInvestmentAccountRuleTypes as RuleTypes,
  paths as accountOverhaulPaths,
} from '@core/schemas/account-overhaul-schema';

/** The backend's NZ-local timestamp shape (no offset), as the `…Nzt` fields carry it. */
const NZT_FORMAT = 'YYYY-MM-DDTHH:mm:ss';

function createRule(
  overrides: Partial<InvestmentAccountRuleDto> & Pick<InvestmentAccountRuleDto, 'investmentAccountRuleId'>,
): InvestmentAccountRuleDto {
  const created = dayjs().subtract(30, 'day').format(NZT_FORMAT);
  const rule: InvestmentAccountRuleDto = {
    investmentAccountRuleType: RuleTypes.Move,
    priority: overrides.investmentAccountRuleId,
    isEnabled: true,
    targetInvestmentAccountId: null,
    targetInvestmentAccountName: null,
    amount: null,
    reference: null,
    schedule: { frequency: RuleFrequency.Monthly, dayOfMonth: 1, lastDayOfMonth: false },
    lastRunNzt: null,
    createdAtNzt: created,
    updatedAtNzt: created,
    disabledAtNzt: null,
    deletedAtNzt: null,
    ...overrides,
  };
  return {
    ...rule,
    nextRunNzt: rule.isEnabled ? nextAutoRuleRun(rule.schedule ?? {}, dayjs()).format(NZT_FORMAT) : null,
  };
}

/**
 * One rule in each card state: an active Auto-move of a set amount, a paused Auto-move, and an active Auto-withdraw
 * of the whole Wallet balance. The Auto-moves target the entity's first account that can take funds, so they name a
 * real account; an entity with none gets only the Auto-withdraw.
 */
async function seedRules(entityId: number, accountsOf: AccountsOf): Promise<InvestmentAccountRuleDto[]> {
  const target = (await accountsOf(entityId)).find(
    account =>
      account.currentStatus !== InvestmentAccountStatuses.Closed &&
      !isWallet(account) &&
      !isLegacyAccount(account) &&
      canReceiveFunds(account, false),
  );
  const moves: InvestmentAccountRuleDto[] = target
    ? [
        createRule({
          investmentAccountRuleId: 1,
          investorEntityId: entityId,
          targetInvestmentAccountId: target.investmentAccountId,
          targetInvestmentAccountName: investmentAccountDisplayName(target),
          amount: 500,
          schedule: { frequency: RuleFrequency.Monthly, dayOfMonth: 1, lastDayOfMonth: false },
        }),
        createRule({
          investmentAccountRuleId: 2,
          investorEntityId: entityId,
          targetInvestmentAccountId: target.investmentAccountId,
          targetInvestmentAccountName: investmentAccountDisplayName(target),
          amount: 1_500,
          isEnabled: false,
          disabledAtNzt: dayjs().subtract(7, 'day').format(NZT_FORMAT),
          schedule: { frequency: RuleFrequency.Weekly, dayOfWeek: DayOfWeek.Monday },
        }),
      ]
    : [];
  return [
    ...moves,
    createRule({
      investmentAccountRuleId: 3,
      investorEntityId: entityId,
      investmentAccountRuleType: RuleTypes.Withdrawal,
      schedule: { frequency: RuleFrequency.Monthly, lastDayOfMonth: true },
    }),
  ];
}

interface RuleInit {
  params?: { path?: { investorEntityId?: number; investmentAccountRuleId?: number } };
  body?: InvestmentAccountRuleForm;
}

/**
 * A mock `ACCOUNT_OVERHAUL_CLIENT` fragment serving the not-yet-live Auto-rule endpoints from a per-entity store,
 * seeded on first touch and kept for the life of the (root-singleton) client, so a pause on one visit is still
 * paused on the next. Composed into `createOverhaulMockClient`; not provided directly by screens.
 */
export function createAutoRulesMockClient(deps: {
  accountsOf: AccountsOf;
  delayMs?: number;
}): Client<accountOverhaulPaths> {
  const wait = () => delay(deps.delayMs ?? randomLatencyMs());
  const stores = new Map<number, Promise<InvestmentAccountRuleDto[]>>();
  const rulesFor = (entityId: number): Promise<InvestmentAccountRuleDto[]> => {
    let rules = stores.get(entityId);
    if (!rules) {
      rules = seedRules(entityId, deps.accountsOf);
      stores.set(entityId, rules);
    }
    return rules;
  };

  return {
    async GET(_path: string, init?: RuleInit) {
      await wait();
      const entityId = init?.params?.path?.investorEntityId;
      if (entityId === undefined) return ok(null);
      // Copies, as a real response would be: handing out the stored objects would let a later pause/resume mutate
      // the query cache in place, and TanStack's structural sharing would then see no change to re-render.
      return ok((await rulesFor(entityId)).map(rule => structuredClone(rule)));
    },

    async POST(path: string, init?: RuleInit) {
      await wait();
      const { investorEntityId, investmentAccountRuleId } = init?.params?.path ?? {};
      if (investorEntityId === undefined) return ok(null);
      const rules = await rulesFor(investorEntityId);

      if (path.endsWith('/investment-account-rules')) {
        // No target account is an Auto-withdraw, which pays to the investor's one bank account.
        const body = init?.body ?? {};
        const targetId = body.targetInvestmentAccountId ?? null;
        const target =
          targetId === null
            ? null
            : ((await deps.accountsOf(investorEntityId)).find(account => account.investmentAccountId === targetId) ??
              null);
        const id = rules.reduce((max, r) => Math.max(max, r.investmentAccountRuleId ?? 0), 0) + 1;
        const created = createRule({
          investmentAccountRuleId: id,
          investorEntityId,
          investmentAccountRuleType: targetId === null ? RuleTypes.Withdrawal : RuleTypes.Move,
          targetInvestmentAccountId: targetId,
          targetInvestmentAccountName: target ? investmentAccountDisplayName(target) : null,
          amount: body.amount ?? null,
          reference: body.reference ?? null,
          schedule: body.schedule,
          createdAtNzt: dayjs().format(NZT_FORMAT),
          updatedAtNzt: dayjs().format(NZT_FORMAT),
        });
        rules.push(created);
        return ok(structuredClone(created));
      }

      const rule = rules.find(r => r.investmentAccountRuleId === investmentAccountRuleId);
      if (!rule) {
        return {
          data: undefined,
          error: [{ errorMessage: 'This rule could not be found' }],
          response: new Response(null, { status: 404 }),
        };
      }

      const now = dayjs();
      if (path.endsWith('/pause')) {
        Object.assign(rule, { isEnabled: false, nextRunNzt: null, disabledAtNzt: now.format(NZT_FORMAT) });
      } else if (path.endsWith('/resume')) {
        Object.assign(rule, {
          isEnabled: true,
          nextRunNzt: nextAutoRuleRun(rule.schedule ?? {}, now).format(NZT_FORMAT),
          disabledAtNzt: null,
        });
      } else {
        throw new Error(`createAutoRulesMockClient: unhandled POST ${path}`);
      }
      rule.updatedAtNzt = now.format(NZT_FORMAT);
      return ok(structuredClone(rule));
    },
    // Cast: this stub implements only the Auto-rule list, create and pause/resume routes, not the whole overhaul surface.
  } as unknown as Client<accountOverhaulPaths>;
}
